import type {
  Post,
  NewPost,
  Category,
  Product,
  Review,
  NewReview,
  Order,
} from '@server/db/schema'
import type { SeoConfig } from '@shared/seo.config'

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  const text = await res.text()
  const payload = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw new ApiError(payload?.error ?? `HTTP ${res.status}`, res.status)
  }
  return payload as T
}

// Tham số rỗng bị bỏ qua thay vì gửi chuỗi trống, vì server phân biệt "không
// lọc" với "lọc bằng giá trị rỗng".
function query(params: Record<string, string | boolean | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const api = {
  health: () => request<{ ok: boolean; database: 'configured' | 'missing' }>('/api/health'),

  seo: {
    get: () => request<SeoConfig>('/api/settings/seo'),
    save: (patch: Partial<SeoConfig>) =>
      request<SeoConfig>('/api/settings/seo', { method: 'PUT', body: JSON.stringify(patch) }),
  },

  posts: {
    list: () => request<Post[]>('/api/posts'),
    get: (id: string) => request<Post>(`/api/posts/${id}`),
    create: (input: Pick<NewPost, 'title' | 'body'>) =>
      request<Post>('/api/posts', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: Partial<NewPost>) =>
      request<Post>(`/api/posts/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) =>
      request<{ ok: true }>(`/api/posts/${id}`, { method: 'DELETE' }),
  },

  categories: {
    list: () => request<Category[]>('/api/categories'),
  },

  products: {
    list: (filters: { categoryId?: string; featured?: boolean } = {}) =>
      request<Product[]>(
        `/api/products${query({ category_id: filters.categoryId, featured: filters.featured })}`,
      ),
    get: (id: string) => request<Product>(`/api/products/${id}`),
    reviews: (id: string) => request<Review[]>(`/api/products/${id}/reviews`),
  },

  reviews: {
    create: (input: Pick<NewReview, 'productId' | 'author' | 'rating' | 'content'>) =>
      request<Review>('/api/reviews', { method: 'POST', body: JSON.stringify(input) }),
  },

  orders: {
    create: (input: {
      customerName: string
      customerEmail: string
      customerPhone: string
      shippingAddress: string
      note?: string
      items: { productId: string; quantity: number }[]
    }) => request<Order>('/api/orders', { method: 'POST', body: JSON.stringify(input) }),
  },
}

export type { Post, NewPost, Category, Product, Review, NewReview, Order, SeoConfig }
